/**
 * MicPrimer
 *
 * The screen iOS does not give us.
 *
 * The system microphone dialog appears exactly once per install, has no room for
 * context, and a "Don't Allow" is permanent. Showing it cold, mid-conversation,
 * is the single easiest way to lose a user forever: the app then looks broken and
 * the fix is buried three levels into Settings. So this explains first, in the
 * user's own language, and only an explicit tap raises the system dialog.
 *
 * If the answer is no, the screen turns into a recovery route rather than a dead
 * end: one button that deep links straight to the app's Settings page.
 *
 * Native only. `NativeMicPrimerGate` renders null in every browser, so the web
 * app never sees this component at all.
 *
 * ORDER RELATIVE TO THE AI CONSENT GATE (WS-7, Guideline 5.1.2(i)):
 * consent comes first, this comes second. Consent is the blocking first-run
 * question about whether audio and text may be sent to third parties at all;
 * this is the narrower "may we open the microphone right now" question, and it
 * is raised lazily, only when a mic entry point is actually used. Asking to
 * capture audio before the user has agreed it may be processed would be both
 * out of order and, on iOS, unrecoverable, since the OS dialog is one-shot.
 *
 * The lazy trigger is what enforces the order: this screen cannot appear before
 * consent unless a mic entry point is reachable while the consent gate is up,
 * which a blocking gate must not allow. This overlay sits at z-index 120; the
 * consent gate must render above it (or as a route that replaces the app shell)
 * so the two can never be on screen together.
 *
 * Deliberately no wording overlap: consent names the processors (Deepgram and
 * the rest) and what they receive. This screen never mentions them, and only
 * explains why the device microphone is needed.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mic, MicOff, Settings as SettingsIcon, Check } from 'lucide-react';
import posthog from 'posthog-js';
import { isNative, isIOSNative } from '@/lib/platform';
import { useAccessibility } from '@/contexts/AccessibilityContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { cn } from '@/lib/utils';
import { pageContentSafeAreaStyle } from '@/lib/safeArea';
import {
  MIC_STATE,
  requestMicAccess,
  markMicPrimerSeen,
  openMicSettings,
} from './micPermission';
import { registerMicPrimer, PRIMER_RESULT } from './micPrimerController';

const BRAND = '#9DDBB9';

/**
 * Presentational + behavioural primer. Exported for reuse (for example a
 * Settings recovery row) as well as for the gate below.
 *
 * @param {object} props
 * @param {(result: string) => void} props.onResolved fired once with the outcome
 */
export function MicPrimer({ onResolved }) {
  const { lightMode, reducedMotion } = useAccessibility();
  const { t } = useLanguage();
  const [phase, setPhase] = useState('intro'); // intro | asking | denied | unavailable
  const resolvedRef = useRef(false);

  const finish = useCallback(
    (result) => {
      if (resolvedRef.current) return;
      resolvedRef.current = true;
      markMicPrimerSeen();
      onResolved?.(result);
    },
    [onResolved],
  );

  useEffect(() => {
    try {
      posthog.capture('native_mic_primer_shown', { platform: isIOSNative() ? 'ios' : 'native' });
    } catch {}
  }, []);

  const handleAllow = useCallback(async () => {
    setPhase('asking');
    const result = await requestMicAccess();
    if (result === MIC_STATE.GRANTED) {
      finish(result);
      return;
    }
    if (result === MIC_STATE.UNAVAILABLE) {
      setPhase('unavailable');
      return;
    }
    setPhase('denied');
  }, [finish]);

  const handleNotNow = useCallback(() => {
    try {
      posthog.capture('native_mic_primer_dismissed', {});
    } catch {}
    finish(PRIMER_RESULT.DISMISSED);
  }, [finish]);

  const handleOpenSettings = useCallback(async () => {
    await openMicSettings();
    // The app is about to lose focus. Resolve so nothing waits on a screen the
    // user can no longer see; the caller re-checks permission on return.
    finish(MIC_STATE.DENIED);
  }, [finish]);

  const title =
    phase === 'denied'
      ? t('native.mic.deniedTitle')
      : phase === 'unavailable'
        ? t('native.mic.unavailableTitle')
        : t('native.mic.primerTitle');

  const body =
    phase === 'denied'
      ? t('native.mic.deniedBody')
      : phase === 'unavailable'
        ? t('native.mic.unavailableBody')
        : t('native.mic.primerBody');

  const points = [
    t('native.mic.primerPoint1'),
    t('native.mic.primerPoint2'),
    t('native.mic.primerPoint3'),
  ];

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className={cn(
        'fixed inset-0 z-[120] flex flex-col items-center justify-center px-6',
        lightMode ? 'bg-white' : 'bg-[#1a1d2e]',
      )}
      style={pageContentSafeAreaStyle}
    >
      <motion.div
        initial={reducedMotion ? {} : { opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-sm flex flex-col items-center text-center"
      >
        <div
          className={cn(
            'w-20 h-20 rounded-full flex items-center justify-center mb-6',
            phase === 'denied' || phase === 'unavailable'
              ? lightMode
                ? 'bg-amber-100'
                : 'bg-amber-500/20'
              : lightMode
                ? 'bg-[#9DDBB9]/25'
                : 'bg-[#9DDBB9]/20',
          )}
        >
          {phase === 'denied' || phase === 'unavailable' ? (
            <MicOff className={cn('w-9 h-9', lightMode ? 'text-amber-600' : 'text-amber-400')} aria-hidden="true" />
          ) : (
            <Mic className="w-9 h-9" style={{ color: BRAND }} aria-hidden="true" />
          )}
        </div>

        <h1 className={cn('text-xl font-semibold mb-3', lightMode ? 'text-gray-900' : 'text-white')}>
          {title}
        </h1>

        <p className={cn('text-[15px] leading-relaxed mb-6', lightMode ? 'text-gray-600' : 'text-white/70')}>
          {body}
        </p>

        {phase !== 'denied' && phase !== 'unavailable' && (
          <ul className="w-full space-y-3 mb-8 text-left">
            {points.map((point) => (
              <li key={point} className="flex items-start gap-3">
                <Check className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: BRAND }} aria-hidden="true" />
                <span className={cn('text-sm', lightMode ? 'text-gray-600' : 'text-white/70')}>{point}</span>
              </li>
            ))}
          </ul>
        )}

        {phase === 'denied' || phase === 'unavailable' ? (
          <div className="w-full space-y-3">
            {phase === 'denied' && (
              <button
                type="button"
                onClick={handleOpenSettings}
                className={cn(
                  'w-full min-h-[56px] py-4 px-6 rounded-xl touch-manipulation',
                  'flex items-center justify-center gap-3 text-lg font-medium',
                  'transition-colors duration-200 focus:outline-none focus:ring-4',
                  'bg-[#9DDBB9] text-gray-800 hover:bg-[#8BCCA8] focus:ring-[#9DDBB9]/50',
                )}
              >
                <SettingsIcon className="w-5 h-5" aria-hidden="true" />
                {t('native.mic.openSettings')}
              </button>
            )}
            <button
              type="button"
              onClick={() => finish(phase === 'denied' ? MIC_STATE.DENIED : MIC_STATE.UNAVAILABLE)}
              className={cn(
                'w-full min-h-[48px] py-3 px-6 rounded-xl touch-manipulation text-sm font-medium',
                'transition-colors duration-200',
                lightMode
                  ? 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
                  : 'text-white/70 hover:text-white hover:bg-white/10',
              )}
            >
              {t('native.mic.continueWithoutMic')}
            </button>
          </div>
        ) : (
          <div className="w-full space-y-3">
            <button
              type="button"
              onClick={handleAllow}
              disabled={phase === 'asking'}
              className={cn(
                'w-full min-h-[56px] py-4 px-6 rounded-xl touch-manipulation',
                'flex items-center justify-center gap-3 text-lg font-medium',
                'transition-colors duration-200 focus:outline-none focus:ring-4',
                phase === 'asking'
                  ? lightMode
                    ? 'bg-gray-200 text-gray-400'
                    : 'bg-white/10 text-white/40'
                  : 'bg-[#9DDBB9] text-gray-800 hover:bg-[#8BCCA8] focus:ring-[#9DDBB9]/50',
              )}
            >
              <Mic className="w-5 h-5" aria-hidden="true" />
              {t('native.mic.primerAllow')}
            </button>
            <button
              type="button"
              onClick={handleNotNow}
              className={cn(
                'w-full min-h-[48px] py-3 px-6 rounded-xl touch-manipulation text-sm font-medium',
                'transition-colors duration-200',
                lightMode
                  ? 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
                  : 'text-white/70 hover:text-white hover:bg-white/10',
              )}
            >
              {t('native.mic.primerNotNow')}
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
}

/**
 * Mount once, near the root. Renders nothing until a native caller awaits
 * `ensureMicPrimed()`, and nothing at all in a browser.
 */
export default function NativeMicPrimerGate() {
  const [open, setOpen] = useState(false);
  const resolveRef = useRef(null);

  useEffect(() => {
    if (!isNative()) return undefined;
    return registerMicPrimer(
      () =>
        new Promise((resolve) => {
          resolveRef.current = resolve;
          setOpen(true);
        }),
    );
  }, []);

  const handleResolved = useCallback((result) => {
    setOpen(false);
    const resolve = resolveRef.current;
    resolveRef.current = null;
    resolve?.(result);
  }, []);

  if (!isNative()) return null;

  return (
    <AnimatePresence>
      {open && <MicPrimer key="mic-primer" onResolved={handleResolved} />}
    </AnimatePresence>
  );
}
